import { useState, useEffect } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ThemeConfig {
  appearance: {
    position: 'bottom-right' | 'bottom-left' | 'side-right' | 'side-left';
    layout?: 'floating' | 'sidebar' | 'expand-to-sidebar' | 'inline';
    launcher: {
      shape: 'circle' | 'rounded-square';
      size: 's' | 'm' | 'l';
    };
    colors: {
      primary: string;
      bg: string;
      text: string;
      border: string;
      muted: string;
      headerText?: string;
    };
    border: {
      width: number;
      radius: number;
      shadow: 0 | 1 | 2 | 3;
    };
    typography: {
      family: 'system' | 'Inter' | 'Manrope';
      scale: 'compact' | 'base' | 'comfortable';
    };
    icon: {
      type: 'tahc' | 'chat-bubble' | 'chat-double' | 'chat-double-outline' | 'custom';
      customSvgUrl?: string;
      iconColor?: string;
      iconBgColor?: string;
    };
    animation: {
      type: 'none' | 'pulse' | 'bounce' | 'slide';
      durationMs: number;
      respectReduceMotion: boolean;
    };
  };
  persona: {
    greeting: string;
    offlineGreeting?: string;
    tone: 'concise' | 'helpful' | 'friendly' | 'professional';
    emoji: boolean;
    handoffLabel: string;
    avatar?: {
      image: string;
      bgColor: string;
    };
  };
  behavior: {
    trigger: {
      type: 'manual' | 'timeOnPage' | 'exitIntent' | 'selector';
      ms?: number;
      selector?: string;
    };
    handoffConfidence: number;
    locale: string;
  };
  accessibility: {
    focusRing: boolean;
    minContrast: 'AA' | 'AAA';
  };
}

interface ThemeEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, config: ThemeConfig) => Promise<void>;
  initialConfig?: ThemeConfig;
  initialName?: string;
  isEditing?: boolean;
}

const defaultConfig: ThemeConfig = {
  appearance: {
    position: 'bottom-right',
    layout: 'floating',
    launcher: {
      shape: 'circle',
      size: 'm',
    },
    colors: {
      primary: '#FF6A00',
      bg: '#FFFFFF',
      text: '#0A0A0A',
      border: '#000000',
      muted: '#666666',
    },
    border: {
      width: 2,
      radius: 24,
      shadow: 2,
    },
    typography: {
      family: 'Inter',
      scale: 'base',
    },
    icon: {
      type: 'tahc',
    },
    animation: {
      type: 'pulse',
      durationMs: 300,
      respectReduceMotion: true,
    },
  },
  persona: {
    greeting: 'How can I help you today?',
    tone: 'helpful',
    emoji: false,
    handoffLabel: 'Talk to a person',
    avatar: {
      image: 'https://media.tahc.ai/profile-pictures/hybrid-chat-done-right-blue-face.webp',
      bgColor: '#FF6A00',
    },
  },
  behavior: {
    trigger: {
      type: 'manual',
    },
    handoffConfidence: 0.45,
    locale: 'en-US',
  },
  accessibility: {
    focusRing: true,
    minContrast: 'AA',
  },
};

export default function ThemeEditor({ isOpen, onClose, onSave, initialConfig, initialName, isEditing = false }: ThemeEditorProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName || '');
  const [config, setConfig] = useState<ThemeConfig>(initialConfig || defaultConfig);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'appearance' | 'persona' | 'behavior'>('appearance');
  const [contrastWarning, setContrastWarning] = useState('');
  const [previousBorderWidth, setPreviousBorderWidth] = useState(2);

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setName(initialName || '');
      setConfig(initialConfig || defaultConfig);
      setContrastWarning('');
      setActiveTab('appearance');
      // Save the current border width when opening the editor
      const currentWidth = (initialConfig || defaultConfig).appearance.border.width;
      if (currentWidth > 0) {
        setPreviousBorderWidth(currentWidth);
      }
    }
  }, [isOpen, initialName, initialConfig]);

  if (!isOpen) return null;

  const updateConfig = (path: string, value: any) => {
    const keys = path.split('.');
    setConfig((prev) => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      let current = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const checkContrast = () => {
    const { text, bg } = config.appearance.colors;
    const ratio = calculateContrast(text, bg);
    const required = config.accessibility.minContrast === 'AAA' ? 7 : 4.5;

    if (ratio < required) {
      setContrastWarning(t('themeEditor.contrastWarning', {
        ratio: ratio.toFixed(2),
        standard: config.accessibility.minContrast,
        required: required
      }));
    } else {
      setContrastWarning('');
    }
  };

  const calculateContrast = (color1: string, color2: string): number => {
    const getLuminance = (color: string): number => {
      const rgb = parseInt(color.replace('#', ''), 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = (rgb >> 0) & 0xff;

      const [rs, gs, bs] = [r, g, b].map((c) => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });

      return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
    };

    const lum1 = getLuminance(color1);
    const lum2 = getLuminance(color2);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);

    return (brightest + 0.05) / (darkest + 0.05);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert(t('themeEditor.enterThemeName'));
      return;
    }

    if (contrastWarning && config.accessibility.minContrast === 'AAA') {
      if (!confirm(t('themeEditor.contrastWarningConfirm'))) {
        return;
      }
    }

    setSaving(true);
    try {
      await onSave(name, config);
      onClose();
    } catch (error) {
      alert(t('themeEditor.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] md:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0 flex-1 pr-2">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 truncate">{isEditing ? t('themeEditor.editTheme') : t('themeEditor.createTheme')}</h2>
            <p className="text-xs md:text-sm text-gray-500 mt-1">{t('themeEditor.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Theme Name */}
        <div className="p-4 md:p-6 border-b border-gray-200 flex-shrink-0">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('themeEditor.themeName')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('themeEditor.themeNamePlaceholder')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          />
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-4 md:px-6 flex-shrink-0 overflow-x-auto">
          <div className="flex gap-4 min-w-max">
            <button
              onClick={() => setActiveTab('appearance')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition whitespace-nowrap ${
                activeTab === 'appearance'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('themeEditor.appearance')}
            </button>
            <button
              onClick={() => setActiveTab('persona')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition whitespace-nowrap ${
                activeTab === 'persona'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('themeEditor.persona')}
            </button>
            <button
              onClick={() => setActiveTab('behavior')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition whitespace-nowrap ${
                activeTab === 'behavior'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('themeEditor.behavior')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 md:p-6 min-h-0">
          {activeTab === 'appearance' && (
            <div className="space-y-6">
              {/* Colors */}
              <div>
                <h3 className="text-lg font-semibold mb-3">{t('themeEditor.colors')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.primaryColor')}</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={config.appearance.colors.primary}
                        onChange={(e) => updateConfig('appearance.colors.primary', e.target.value)}
                        className="w-16 h-12 md:w-12 md:h-10 rounded border border-gray-300 cursor-pointer flex-shrink-0"
                      />
                      <input
                        type="text"
                        value={config.appearance.colors.primary}
                        onChange={(e) => updateConfig('appearance.colors.primary', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.background')}</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={config.appearance.colors.bg}
                        onChange={(e) => {
                          updateConfig('appearance.colors.bg', e.target.value);
                          setTimeout(checkContrast, 100);
                        }}
                        className="w-16 h-12 md:w-12 md:h-10 rounded border border-gray-300 cursor-pointer flex-shrink-0"
                      />
                      <input
                        type="text"
                        value={config.appearance.colors.bg}
                        onChange={(e) => {
                          updateConfig('appearance.colors.bg', e.target.value);
                          setTimeout(checkContrast, 100);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.textColor')}</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={config.appearance.colors.text}
                        onChange={(e) => {
                          updateConfig('appearance.colors.text', e.target.value);
                          setTimeout(checkContrast, 100);
                        }}
                        className="w-16 h-12 md:w-12 md:h-10 rounded border border-gray-300 cursor-pointer flex-shrink-0"
                      />
                      <input
                        type="text"
                        value={config.appearance.colors.text}
                        onChange={(e) => {
                          updateConfig('appearance.colors.text', e.target.value);
                          setTimeout(checkContrast, 100);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.borderColor')}</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={config.appearance.colors.border}
                        onChange={(e) => updateConfig('appearance.colors.border', e.target.value)}
                        className="w-16 h-12 md:w-12 md:h-10 rounded border border-gray-300 cursor-pointer flex-shrink-0"
                      />
                      <input
                        type="text"
                        value={config.appearance.colors.border}
                        onChange={(e) => updateConfig('appearance.colors.border', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.headerTextColor')}</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={config.appearance.colors.headerText || '#ffffff'}
                        onChange={(e) => updateConfig('appearance.colors.headerText', e.target.value)}
                        className="w-16 h-12 md:w-12 md:h-10 rounded border border-gray-300 cursor-pointer flex-shrink-0"
                      />
                      <input
                        type="text"
                        value={config.appearance.colors.headerText || ''}
                        onChange={(e) => updateConfig('appearance.colors.headerText', e.target.value)}
                        placeholder={t('themeEditor.headerTextPlaceholder')}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{t('themeEditor.headerTextHelp')}</p>
                  </div>
                </div>
                {contrastWarning && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-800">{contrastWarning}</p>
                  </div>
                )}
              </div>

              {/* Layout Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.layout')}</label>
                <select
                  value={config.appearance.layout || 'floating'}
                  onChange={(e) => updateConfig('appearance.layout', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="floating">{t('themeEditor.layoutFloating')}</option>
                  <option value="sidebar">{t('themeEditor.layoutSidebar')}</option>
                  <option value="expand-to-sidebar">{t('themeEditor.layoutExpandToSidebar')}</option>
                  <option value="inline">{t('themeEditor.layoutInline')}</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">{t('themeEditor.layoutHelp')}</p>

                {/* Inline layout instructions */}
                {config.appearance.layout === 'inline' && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-2">{t('themeEditor.inlineSetupTitle')}</p>
                    <p className="text-xs text-blue-700 mb-2">{t('themeEditor.inlineSetupDesc')}</p>
                    <code className="block text-xs bg-blue-100 p-2 rounded font-mono text-blue-900">
                      {'<div id="tahc-webchat-embed"></div>'}
                    </code>
                    <p className="text-xs text-blue-600 mt-2">{t('themeEditor.inlineSetupCustom')}</p>
                  </div>
                )}
              </div>

              {/* Position & Launcher */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.position')}</label>
                  <select
                    value={config.appearance.position}
                    onChange={(e) => updateConfig('appearance.position', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="bottom-right">{t('themeEditor.positionBottomRight')}</option>
                    <option value="bottom-left">{t('themeEditor.positionBottomLeft')}</option>
                    <option value="side-right">{t('themeEditor.positionSideRight')}</option>
                    <option value="side-left">{t('themeEditor.positionSideLeft')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.launcherShape')}</label>
                  <select
                    value={config.appearance.launcher.shape}
                    onChange={(e) => updateConfig('appearance.launcher.shape', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="circle">{t('themeEditor.shapeCircle')}</option>
                    <option value="rounded-square">{t('themeEditor.shapeRoundedSquare')}</option>
                  </select>
                </div>
              </div>

              {/* Icon Settings */}
              <div>
                <h3 className="text-lg font-semibold mb-3">{t('themeEditor.chatIcon')}</h3>
                <div className="space-y-4">
                  {/* Icon Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">{t('themeEditor.iconStyle')}</label>
                    <div className="grid grid-cols-4 gap-3">
                      <button
                        type="button"
                        onClick={() => updateConfig('appearance.icon.type', 'tahc')}
                        className={`p-4 border-2 rounded-lg transition flex flex-col items-center gap-2 ${
                          config.appearance.icon.type === 'tahc'
                            ? 'border-black bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <svg width="48" height="48" viewBox="0 0 1024 1024" fill="none">
                          <rect width="1024" height="1024" rx={config.appearance.launcher.shape === 'circle' ? '512' : '192'} fill={config.appearance.icon.iconBgColor || config.appearance.colors.primary} stroke={config.appearance.colors.border} strokeWidth={config.appearance.border.width * 16}/>
                          <path d="M492.314 258.068H427.984C312.123 258.069 217.855 350.528 214.936 465.686L214.866 471.188C214.867 588.889 310.283 684.306 427.984 684.307H492.314V766.892H492.176V895.024L393.771 764.932C246.575 747.972 132.281 622.928 132.28 471.188L132.304 467.368C134.351 305.817 265.948 175.485 427.984 175.483H492.314V258.068ZM391.855 347.673C391.864 347.782 395.32 393.087 398.689 407.831C400.435 415.397 405.703 427.817 412.094 436.068C417.716 443.292 424.582 449.516 432.423 454.456C439.465 458.91 449.525 463.164 455.738 464.677C467.515 467.533 503.851 471.186 503.851 471.186C503.851 471.186 467.515 474.842 455.738 477.697C449.525 479.21 439.465 483.492 432.423 487.918C424.552 492.857 417.686 499.081 412.094 506.304C405.703 514.555 400.435 526.947 398.689 534.542C395.293 549.306 391.867 594.546 391.855 594.701C391.844 594.548 388.388 549.278 385.021 534.542C383.275 526.975 378.008 514.555 371.617 506.304C366.025 499.081 359.158 492.857 351.317 487.918C344.275 483.464 334.215 479.21 328.002 477.697C316.225 474.842 279.89 471.186 279.89 471.186C279.89 471.186 316.225 467.533 328.002 464.677C334.216 463.164 344.275 458.881 351.317 454.456C359.159 449.516 366.025 443.292 371.617 436.068C378.008 427.817 383.275 415.425 385.021 407.831C388.419 393.058 391.848 347.777 391.855 347.673Z" fill={config.appearance.icon.iconColor || '#FFFFFF'}/>
                          <path d="M596.575 175.482C759.888 175.482 892.28 307.874 892.28 471.187L892.257 475.006C890.21 636.558 758.613 766.891 596.575 766.892H532.245V684.306H596.575C712.437 684.305 806.705 591.847 809.625 476.689L809.694 471.187C809.694 353.485 714.277 258.068 596.575 258.068H532.245V175.482H596.575ZM626.386 396.891C667.418 396.891 700.682 430.154 700.682 471.187C700.681 512.219 667.418 545.483 626.386 545.483C585.353 545.483 552.09 512.219 552.09 471.187C552.09 430.154 585.353 396.891 626.386 396.891Z" fill={config.appearance.icon.iconColor || '#FFFFFF'}/>
                        </svg>
                        <span className="text-xs font-medium">{t('themeEditor.tahcIcon')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateConfig('appearance.icon.type', 'chat-bubble')}
                        className={`p-4 border-2 rounded-lg transition flex flex-col items-center gap-2 ${
                          config.appearance.icon.type === 'chat-bubble'
                            ? 'border-black bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
                          <rect width="64" height="64" rx={config.appearance.launcher.shape === 'circle' ? '32' : '12'} fill={config.appearance.icon.iconBgColor || config.appearance.colors.primary} stroke={config.appearance.colors.border} strokeWidth={config.appearance.border.width}/>
                          <path d="M16 24c0-2.2 1.8-4 4-4h24c2.2 0 4 1.8 4 4v16c0 2.2-1.8 4-4 4H28l-6 6v-6h-2c-2.2 0-4-1.8-4-4V24z" fill={config.appearance.icon.iconColor || '#FFFFFF'}/>
                          <circle cx="24" cy="32" r="2" fill={config.appearance.icon.iconBgColor || config.appearance.colors.primary}/>
                          <circle cx="32" cy="32" r="2" fill={config.appearance.icon.iconBgColor || config.appearance.colors.primary}/>
                          <circle cx="40" cy="32" r="2" fill={config.appearance.icon.iconBgColor || config.appearance.colors.primary}/>
                        </svg>
                        <span className="text-xs font-medium">{t('themeEditor.chatBubble')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateConfig('appearance.icon.type', 'chat-double')}
                        className={`p-4 border-2 rounded-lg transition flex flex-col items-center gap-2 ${
                          config.appearance.icon.type === 'chat-double'
                            ? 'border-black bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
                          <rect width="64" height="64" rx={config.appearance.launcher.shape === 'circle' ? '32' : '12'} fill={config.appearance.icon.iconBgColor || config.appearance.colors.primary} stroke={config.appearance.colors.border} strokeWidth={config.appearance.border.width}/>
                          <path d="M22 18C19.7909 18 18 19.7909 18 22V38C18 40.2091 19.7909 42 22 42H34L38 46V42H46C48.2091 42 50 40.2091 50 38V22C50 19.7909 48.2091 18 46 18H22Z" fill="rgba(255,255,255,0.5)"/>
                          <path d="M14 24C14 21.7909 15.7909 20 18 20H38C40.2091 20 42 21.7909 42 24V36C42 38.2091 40.2091 40 38 40H30L26 44V40H18C15.7909 40 14 38.2091 14 36V24Z" fill={config.appearance.icon.iconColor || '#FFFFFF'}/>
                        </svg>
                        <span className="text-xs font-medium">{t('themeEditor.doubleChat')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateConfig('appearance.icon.type', 'chat-double-outline')}
                        className={`p-4 border-2 rounded-lg transition flex flex-col items-center gap-2 ${
                          config.appearance.icon.type === 'chat-double-outline'
                            ? 'border-black bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
                          <rect width="64" height="64" rx={config.appearance.launcher.shape === 'circle' ? '32' : '12'} fill={config.appearance.icon.iconBgColor || config.appearance.colors.primary} stroke={config.appearance.colors.border} strokeWidth={config.appearance.border.width}/>
                          <path d="M22 20C19.7909 20 18 21.7909 18 24V36C18 38.2091 19.7909 40 22 40H30L34 44V40H42C44.2091 40 46 38.2091 46 36V24C46 21.7909 44.2091 20 42 20H22Z" stroke={config.appearance.icon.iconColor || '#FFFFFF'} strokeWidth="2" fill="none"/>
                          <path d="M18 26C18 23.7909 19.7909 22 22 22H38C40.2091 22 42 23.7909 42 24V38C42 40.2091 40.2091 42 38 42H30L26 46V42H22C19.7909 42 18 40.2091 18 38V26Z" stroke={config.appearance.icon.iconColor || '#FFFFFF'} strokeWidth="2.5" fill="none"/>
                        </svg>
                        <span className="text-xs font-medium">{t('themeEditor.outlineChat')}</span>
                      </button>
                    </div>
                  </div>

                  {/* Icon Colors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.iconColor')}</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={config.appearance.icon.iconColor || config.appearance.colors.primary}
                          onChange={(e) => updateConfig('appearance.icon.iconColor', e.target.value)}
                          className="w-16 h-12 md:w-12 md:h-10 rounded border border-gray-300 cursor-pointer flex-shrink-0"
                        />
                        <input
                          type="text"
                          value={config.appearance.icon.iconColor || config.appearance.colors.primary}
                          onChange={(e) => updateConfig('appearance.icon.iconColor', e.target.value)}
                          placeholder={t('themeEditor.iconColorPlaceholder')}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{t('themeEditor.iconColorHelp')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Border Settings */}
              <div>
                <h3 className="text-lg font-semibold mb-3">{t('themeEditor.border')}</h3>
                <div className="space-y-4">
                  {/* Show Border Toggle */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="showBorder"
                      checked={config.appearance.border.width > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Restore previous width when enabling
                          updateConfig('appearance.border.width', previousBorderWidth);
                        } else {
                          // Save current width before disabling
                          if (config.appearance.border.width > 0) {
                            setPreviousBorderWidth(config.appearance.border.width);
                          }
                          updateConfig('appearance.border.width', 0);
                        }
                      }}
                      className="w-4 h-4 text-black border-gray-300 rounded"
                    />
                    <label htmlFor="showBorder" className="text-sm font-medium text-gray-700">
                      {t('themeEditor.showBorder')}
                    </label>
                  </div>

                  {/* Border Configuration (shown when border is enabled) */}
                  {config.appearance.border.width > 0 && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.borderWidth')}</label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={config.appearance.border.width}
                          onChange={(e) => updateConfig('appearance.border.width', Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.borderRadius')}</label>
                        <input
                          type="number"
                          min="0"
                          max="50"
                          value={config.appearance.border.radius}
                          onChange={(e) => updateConfig('appearance.border.radius', Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.borderShadow')}</label>
                        <select
                          value={config.appearance.border.shadow}
                          onChange={(e) => updateConfig('appearance.border.shadow', Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="0">{t('themeEditor.shadowNone')}</option>
                          <option value="1">{t('themeEditor.shadowSmall')}</option>
                          <option value="2">{t('themeEditor.shadowMedium')}</option>
                          <option value="3">{t('themeEditor.shadowLarge')}</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Typography & Animation */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.fontFamily')}</label>
                  <select
                    value={config.appearance.typography.family}
                    onChange={(e) => updateConfig('appearance.typography.family', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="system">{t('themeEditor.fontSystem')}</option>
                    <option value="Inter">{t('themeEditor.fontInter')}</option>
                    <option value="Manrope">{t('themeEditor.fontManrope')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.animation')}</label>
                  <select
                    value={config.appearance.animation.type}
                    onChange={(e) => updateConfig('appearance.animation.type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="none">{t('themeEditor.animationNone')}</option>
                    <option value="pulse">{t('themeEditor.animationPulse')}</option>
                    <option value="bounce">{t('themeEditor.animationBounce')}</option>
                    <option value="slide">{t('themeEditor.animationSlide')}</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'persona' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.greetingMessage')}</label>
                <input
                  type="text"
                  value={config.persona.greeting}
                  onChange={(e) => updateConfig('persona.greeting', e.target.value)}
                  placeholder={t('themeEditor.greetingPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.offlineGreeting')}</label>
                <input
                  type="text"
                  value={config.persona.offlineGreeting || ''}
                  onChange={(e) => updateConfig('persona.offlineGreeting', e.target.value)}
                  placeholder={t('themeEditor.offlineGreetingPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.tone')}</label>
                <select
                  value={config.persona.tone}
                  onChange={(e) => updateConfig('persona.tone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="concise">{t('themeEditor.toneConcise')}</option>
                  <option value="helpful">{t('themeEditor.toneHelpful')}</option>
                  <option value="friendly">{t('themeEditor.toneFriendly')}</option>
                  <option value="professional">{t('themeEditor.toneProfessional')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.handoffLabel')}</label>
                <input
                  type="text"
                  value={config.persona.handoffLabel}
                  onChange={(e) => updateConfig('persona.handoffLabel', e.target.value)}
                  placeholder={t('themeEditor.handoffPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="emoji"
                  checked={config.persona.emoji}
                  onChange={(e) => updateConfig('persona.emoji', e.target.checked)}
                  className="w-4 h-4 text-black border-gray-300 rounded"
                />
                <label htmlFor="emoji" className="text-sm font-medium text-gray-700">
                  {t('themeEditor.allowEmoji')}
                </label>
              </div>

              {/* AI Avatar Settings */}
              <div>
                <h3 className="text-lg font-semibold mb-3">{t('themeEditor.aiAvatar')}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">{t('themeEditor.avatarImage')}</label>
                    <div className="grid grid-cols-4 gap-3">
                      <button
                        type="button"
                        onClick={() => updateConfig('persona.avatar', {
                          image: 'https://dev-app.tahc.ai/tahc-logo-2026.svg',
                          bgColor: config.persona.avatar?.bgColor || '#FFFFFF'
                        })}
                        className={`p-4 border-2 rounded-lg transition flex flex-col items-center gap-2 ${
                          config.persona.avatar?.image === 'https://dev-app.tahc.ai/tahc-logo-2026.svg'
                            ? 'border-black bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: config.persona.avatar?.bgColor || '#FFFFFF' }}
                        >
                          <img
                            src="https://dev-app.tahc.ai/tahc-logo-2026.svg"
                            alt={t('themeEditor.avatarTahcLogo')}
                            className="w-12 h-12 object-contain"
                          />
                        </div>
                        <span className="text-xs font-medium">{t('themeEditor.avatarTahcLogo')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateConfig('persona.avatar', {
                          image: 'https://media.tahc.ai/profile-pictures/hybrid-chat-done-right-blue-face.webp',
                          bgColor: config.persona.avatar?.bgColor || '#FF6A00'
                        })}
                        className={`p-4 border-2 rounded-lg transition flex flex-col items-center gap-2 ${
                          (config.persona.avatar?.image || 'https://media.tahc.ai/profile-pictures/hybrid-chat-done-right-blue-face.webp') === 'https://media.tahc.ai/profile-pictures/hybrid-chat-done-right-blue-face.webp'
                            ? 'border-black bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: config.persona.avatar?.bgColor || '#FF6A00' }}
                        >
                          <img
                            src="https://media.tahc.ai/profile-pictures/hybrid-chat-done-right-blue-face.webp"
                            alt={t('themeEditor.avatarBlueFace')}
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                        <span className="text-xs font-medium">{t('themeEditor.avatarBlueFace')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateConfig('persona.avatar', {
                          image: 'https://media.tahc.ai/profile-pictures/hybrid-chat-ai-human-robot-pro-woman.webp',
                          bgColor: config.persona.avatar?.bgColor || '#FF6A00'
                        })}
                        className={`p-4 border-2 rounded-lg transition flex flex-col items-center gap-2 ${
                          config.persona.avatar?.image === 'https://media.tahc.ai/profile-pictures/hybrid-chat-ai-human-robot-pro-woman.webp'
                            ? 'border-black bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: config.persona.avatar?.bgColor || '#FF6A00' }}
                        >
                          <img
                            src="https://media.tahc.ai/profile-pictures/hybrid-chat-ai-human-robot-pro-woman.webp"
                            alt={t('themeEditor.avatarProWoman')}
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                        <span className="text-xs font-medium">{t('themeEditor.avatarProWoman')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateConfig('persona.avatar', {
                          image: 'https://media.tahc.ai/profile-pictures/hybrid-chat-ai-human-robot-pro-man.webp',
                          bgColor: config.persona.avatar?.bgColor || '#FF6A00'
                        })}
                        className={`p-4 border-2 rounded-lg transition flex flex-col items-center gap-2 ${
                          config.persona.avatar?.image === 'https://media.tahc.ai/profile-pictures/hybrid-chat-ai-human-robot-pro-man.webp'
                            ? 'border-black bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: config.persona.avatar?.bgColor || '#FF6A00' }}
                        >
                          <img
                            src="https://media.tahc.ai/profile-pictures/hybrid-chat-ai-human-robot-pro-man.webp"
                            alt={t('themeEditor.avatarProMan')}
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                        <span className="text-xs font-medium">{t('themeEditor.avatarProMan')}</span>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.avatarBgColor')}</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={config.persona.avatar?.bgColor || '#FF6A00'}
                        onChange={(e) => updateConfig('persona.avatar', {
                          image: config.persona.avatar?.image || 'https://media.tahc.ai/profile-pictures/hybrid-chat-done-right-blue-face.webp',
                          bgColor: e.target.value
                        })}
                        className="w-16 h-12 md:w-12 md:h-10 rounded border border-gray-300 cursor-pointer flex-shrink-0"
                      />
                      <input
                        type="text"
                        value={config.persona.avatar?.bgColor || '#FF6A00'}
                        onChange={(e) => updateConfig('persona.avatar', {
                          image: config.persona.avatar?.image || 'https://media.tahc.ai/profile-pictures/hybrid-chat-done-right-blue-face.webp',
                          bgColor: e.target.value
                        })}
                        placeholder={t('themeEditor.avatarBgPlaceholder')}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'behavior' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.triggerType')}</label>
                <select
                  value={config.behavior.trigger.type}
                  onChange={(e) => updateConfig('behavior.trigger.type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="manual">{t('themeEditor.triggerManual')}</option>
                  <option value="timeOnPage">{t('themeEditor.triggerTimeOnPage')}</option>
                  <option value="exitIntent">{t('themeEditor.triggerExitIntent')}</option>
                  <option value="selector">{t('themeEditor.triggerSelector')}</option>
                </select>
              </div>
              {config.behavior.trigger.type === 'timeOnPage' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.delaySeconds')}</label>
                  <input
                    type="number"
                    min="1"
                    value={(config.behavior.trigger.ms || 5000) / 1000}
                    onChange={(e) => updateConfig('behavior.trigger.ms', Number(e.target.value) * 1000)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.handoffConfidence')}</label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={config.behavior.handoffConfidence}
                  onChange={(e) => updateConfig('behavior.handoffConfidence', Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">{t('themeEditor.handoffConfidenceHelp')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.accessibility')}</label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="focusRing"
                      checked={config.accessibility.focusRing}
                      onChange={(e) => updateConfig('accessibility.focusRing', e.target.checked)}
                      className="w-4 h-4 text-black border-gray-300 rounded"
                    />
                    <label htmlFor="focusRing" className="text-sm font-medium text-gray-700">
                      {t('themeEditor.showFocusRings')}
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('themeEditor.minContrast')}</label>
                    <select
                      value={config.accessibility.minContrast}
                      onChange={(e) => {
                        updateConfig('accessibility.minContrast', e.target.value);
                        setTimeout(checkContrast, 100);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="AA">{t('themeEditor.contrastAA')}</option>
                      <option value="AAA">{t('themeEditor.contrastAAA')}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 md:p-6 border-t border-gray-200 flex items-center justify-end gap-2 md:gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition text-sm md:text-base"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 md:px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50 text-sm md:text-base"
          >
            <Check className="w-4 h-4" />
            <span>{saving ? t('themeEditor.saving') : (isEditing ? t('themeEditor.updateTheme') : t('themeEditor.createTheme'))}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
